/**
 * Design-board route (clawinstall-public) — eagerly registers the file route with
 * its production gate and search validation; the frame registry and all 16
 * board components are deferred to the `.lazy.tsx` twin so the scoped
 * capture CSS and the frame components never reach the always-loaded main
 * chunk.
 *
 * The board renders the frozen design capture in docs/design/clawinstall-public for
 * the pixel-convergence loop (scripts/design-board-sweep.ts) and for
 * eyeballing frames during /design-wire. It is not a product surface:
 * production requests 404 via `beforeLoad`, while dev (`development`) and
 * preview (`preview`) keep it reachable from the same build artifact.
 *
 * @since 1.0.0
 */
import { createFileRoute, notFound } from "@tanstack/react-router";

import publicFontsCss from "~/components/design/clawinstall-public/fonts.css?url";
import publicCss from "~/components/design/clawinstall-public/public.css?url";
import { runtimeNodeEnv, shouldHideDesignBoard, validateDesignBoardSearch } from "~/lib/design-board.ts";

/**
 * The board's stylesheets as render-blocking head links, in the board's own
 * load order: the board-only `fonts.css` (the capture's Google Fonts request,
 * so rasterization matches the frozen PNGs on the FlirtMachine-branded
 * localhost) first, then the scoped capture sheet. Linked here rather than
 * side-effect-imported from the design components so the sheets are in the
 * SSR head (no flash of unstyled frames) and so `fonts.css` never ships with
 * the production pages, which load their fonts through `config/head.ts`.
 *
 * @since 1.0.0
 */
export const designBoardStylesheetLinks = [
  { rel: "stylesheet", href: publicFontsCss },
  { rel: "stylesheet", href: publicCss },
];

export const Route = createFileRoute("/design-board/clawinstall-public")({
  beforeLoad: () => {
    if (shouldHideDesignBoard(runtimeNodeEnv())) {
      throw notFound();
    }
  },
  validateSearch: validateDesignBoardSearch,
  head: () => ({ links: designBoardStylesheetLinks }),
});
